import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Send, Train, Utensils } from "lucide-react";
import { Brief, Plan, Tweak, tweaks } from "./data";
import { money } from "./shared";
import { coverFor, detailsFor } from "./film";
import Reveal from "./Reveal";
import { cn } from "@/lib/utils";

function Bar({ value, tone = "primary" }: { value: number; tone?: "primary" | "accent" }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <motion.div
        className={cn("h-full", tone === "accent" ? "bg-accent" : "bg-primary")}
        initial={{ width: 0 }}
        whileInView={{ width: `${Math.min(100, value)}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      />
    </div>
  );
}

export default function PlanDetail({
  plan,
  brief,
  onBack,
}: {
  plan: Plan;
  brief: Brief;
  onBack: () => void;
}) {
  const [applied, setApplied] = useState<Tweak[]>([]);
  const [draft, setDraft] = useState("");
  const [detailA, detailB] = detailsFor(plan.id);

  const total = useMemo(
    () => Math.max(400, plan.total + applied.reduce((s, t) => s + t.deltaTotal, 0)),
    [plan.total, applied]
  );
  const perPerson = Math.round(total / Math.max(1, brief.teamSize));
  const budgetScale = total / plan.total;
  const overBudget = total > brief.budgetTotal;
  const changes = applied.flatMap((t) => t.changes);
  const remaining = tweaks.filter((t) => !applied.some((a) => a.id === t.id));

  const apply = (t: Tweak) => setApplied((a) => [...a, t]);

  const submitDraft = () => {
    const text = draft.trim().toLowerCase();
    if (!text) return;
    const match =
      remaining.find((t) => t.label.toLowerCase().includes(text)) ??
      remaining.find((t) =>
        text.split(/\s+/).some((w) => w.length > 3 && t.label.toLowerCase().includes(w))
      ) ??
      remaining[0];
    if (match) apply(match);
    setDraft("");
  };

  return (
    <article className="pb-28 tablet:pb-28 mobile:pb-28">
      {/* film poster header */}
      <header className="relative h-[62svh] min-h-[420px] overflow-hidden">
        <img
          src={coverFor(plan.id)}
          alt={plan.name}
          width={1280}
          height={1600}
          className="absolute inset-0 h-full w-full object-cover film-tone"
        />
        <div className="absolute inset-0 film-grain" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-primary/20" />

        <div className="relative container mx-auto px-4 laptop:px-6 desktop:px-6 h-full flex flex-col justify-between py-8">
          <button
            onClick={onBack}
            className="inline-flex w-fit items-center gap-2 text-sm text-primary-foreground/85 hover:text-primary-foreground transition-colors"
          >
            <ArrowLeft size={15} /> back to the three plans
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[11px] uppercase tracking-[0.2em] text-primary-foreground/80 mb-4">
              {plan.location}
            </p>
            <h1 className="font-funnel font-bold text-primary-foreground text-3xl tablet:text-5xl laptop:text-6xl desktop:text-6xl mb-5">
              {plan.name}
            </h1>
            <p className="max-w-2xl text-primary-foreground/85">{plan.concept}</p>
          </motion.div>
        </div>
      </header>

      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
        {/* at a glance */}
        <Reveal className="grid mobile:grid-cols-2 tablet:grid-cols-2 laptop:grid-cols-4 desktop:grid-cols-4 gap-8 border-b border-border py-10">
          {[
            ["total", money(total)],
            ["per person", money(perPerson)],
            ["travel", plan.travelLabel],
            ["balance", `${plan.balance} / ${100 - plan.balance}`],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">{k}</p>
              <p className="font-funnel font-bold text-xl text-foreground">{v}</p>
            </div>
          ))}
        </Reveal>

        {changes.length > 0 && (
          <Reveal className="py-10 border-b border-border">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
              updated by your tweaks
            </p>
            <div className="space-y-2">
              {changes.map((c, i) => (
                <motion.p
                  key={`${c.field}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-sm text-foreground"
                >
                  <span className="text-muted-foreground">{c.field}: </span>
                  {c.value}
                </motion.p>
              ))}
            </div>
          </Reveal>
        )}

        {/* the day */}
        <section className="border-b border-border py-16">
          <Reveal>
            <h2 className="font-funnel font-bold text-2xl tablet:text-3xl text-foreground mb-10 flex items-center gap-3">
              <Clock size={20} className="text-primary" /> the day
            </h2>
          </Reveal>
          <ol className="relative border-l border-border pl-7 space-y-9 max-w-2xl">
            {plan.timeline.map((t, i) => (
              <Reveal key={t.time} delay={i * 0.05}>
                <li className="relative">
                  <span className="absolute -left-[34px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <p className="font-funnel text-sm text-muted-foreground mb-1">{t.time}</p>
                  <p className="font-funnel font-bold text-foreground mb-1">{t.title}</p>
                  <p className="text-sm text-muted-foreground">{t.detail}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </section>
      </div>

      {/* full bleed film break */}
      <Reveal y={0}>
        <div className="relative h-[42svh] min-h-[260px] overflow-hidden">
          <img
            src={detailA}
            alt="the evening"
            loading="lazy"
            width={1600}
            height={1088}
            className="h-full w-full object-cover film-tone"
          />
          <div className="absolute inset-0 film-grain" />
        </div>
      </Reveal>

      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
        {/* venue, activity, evening, food */}
        <section className="border-b border-border py-16 grid mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-2 desktop:grid-cols-2 gap-6">
          {[
            { label: "venue", title: plan.venue.name, body: plan.venue.detail, sub: plan.venue.capacity },
            { label: "main activity", title: plan.activity.name, body: plan.activity.detail },
            { label: "optional evening", title: plan.evening.name, body: plan.evening.detail },
          ].map((c, i) => (
            <Reveal key={c.label} delay={i * 0.06}>
              <div className="rounded-2xl border border-border bg-card p-7 h-full">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  {c.label}
                </p>
                <p className="font-funnel font-bold text-foreground mb-2">{c.title}</p>
                <p className="text-sm text-foreground/85">{c.body}</p>
                {c.sub && <p className="text-sm text-muted-foreground mt-3">{c.sub}</p>}
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.18}>
            <div className="rounded-2xl border border-border bg-card p-7 h-full">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-2">
                <Utensils size={12} /> food
              </p>
              <ul className="space-y-2.5">
                {plan.food.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-foreground/90">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>

        {/* travel + budget */}
        <section className="border-b border-border py-16 grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-funnel font-bold text-2xl text-foreground mb-8 flex items-center gap-3">
              <Train size={18} className="text-primary" /> travel
            </h2>
            <div className="space-y-5">
              {plan.travel.map((t) => {
                const mins = t.time.includes("h")
                  ? parseInt(t.time) * 60 + (parseInt(t.time.split("h")[1]) || 0)
                  : parseInt(t.time) || 25;
                const width = Math.min(100, (mins / 210) * 100);
                return (
                  <div key={t.city}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-foreground">
                        {t.city}{" "}
                        <span className="text-muted-foreground">
                          · {t.people} {t.people === 1 ? "person" : "people"}
                        </span>
                      </span>
                      <span className="text-muted-foreground">
                        {t.time} · {t.cost === 0 ? "local" : money(t.cost)}
                      </span>
                    </div>
                    <Bar value={width} tone={width > 70 ? "accent" : "primary"} />
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="font-funnel font-bold text-2xl text-foreground mb-8">the budget</h2>
            <div className="space-y-3.5 mb-6">
              {plan.budget.map((b) => {
                const amount = Math.round(b.amount * budgetScale);
                return (
                  <div key={b.label}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-foreground">{b.label}</span>
                      <span className="text-muted-foreground">{money(amount)}</span>
                    </div>
                    <Bar value={(amount / total) * 100} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-end justify-between border-t border-border pt-5">
              <span className="text-sm text-muted-foreground">
                estimated total {overBudget ? "· over your budget" : "· within your budget"}
              </span>
              <div className="text-right">
                <motion.p
                  key={total}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-funnel font-bold text-xl text-foreground"
                >
                  {money(total)}
                </motion.p>
                <p className="text-sm text-muted-foreground">{money(perPerson)} pp</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              illustrative estimates, not quotes.
            </p>
          </Reveal>
        </section>
      </div>

      <Reveal y={0}>
        <div className="relative h-[36svh] min-h-[220px] overflow-hidden">
          <img
            src={detailB}
            alt="the detail"
            loading="lazy"
            width={1600}
            height={1088}
            className="h-full w-full object-cover film-tone"
          />
          <div className="absolute inset-0 film-grain" />
        </div>
      </Reveal>

      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
        {/* tweak composer */}
        <section className="border-b border-border py-16 grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-[1fr_1fr] desktop:grid-cols-[1fr_1fr] gap-12">
          <Reveal>
            <h2 className="font-funnel font-bold text-2xl tablet:text-3xl text-foreground mb-4">
              want it{" "}
              <span className="font-cormorant italic font-normal">different?</span>
            </h2>
            <p className="text-muted-foreground mb-7 max-w-md">
              ask in plain words and the plan updates in place - cheaper, calmer, outdoors, an extra
              night.
            </p>

            <div className="flex flex-wrap gap-2 mb-5">
              {remaining.slice(0, 3).map((t) => (
                <button
                  key={t.id}
                  onClick={() => apply(t)}
                  className="rounded-full border border-border px-4 py-2 text-xs text-foreground hover:border-primary/60 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 max-w-md">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitDraft()}
                placeholder="make it cheaper, more relaxed..."
                className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
              />
              <button
                onClick={submitDraft}
                className="h-10 w-10 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
                aria-label="send tweak"
              >
                <Send size={14} />
              </button>
            </div>

            {applied.length > 0 && (
              <div className="mt-7 space-y-3 border-t border-border pt-6 max-w-md">
                {applied.map((t) => (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                    <p className="text-sm text-foreground mb-1">{t.label}</p>
                    <p className="text-sm text-muted-foreground">{t.reply}</p>
                  </motion.div>
                ))}
                <button
                  onClick={() => setApplied([])}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  reset this plan
                </button>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.08}>
            <div className="rounded-2xl border border-border bg-card p-7 space-y-7">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  best for
                </p>
                <p className="text-foreground">{plan.bestFor}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  tradeoff
                </p>
                <p className="text-sm text-foreground/85">{plan.tradeoff}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  not included
                </p>
                <ul className="space-y-2">
                  {plan.notIncluded.map((n) => (
                    <li key={n} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-border shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  assumptions
                </p>
                <ul className="space-y-2">
                  {plan.assumptions.map((a) => (
                    <li key={a} className="flex gap-3 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-border shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </section>
      </div>

      {/* sticky summary bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-funnel font-bold text-foreground truncate">{money(total)}</p>
            <p className="text-xs text-muted-foreground truncate">
              {money(perPerson)} per person · {brief.teamSize} people
            </p>
          </div>
          <button
            onClick={onBack}
            className="shrink-0 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90 transition-colors"
          >
            back to the three plans
          </button>
        </div>
      </div>
    </article>
  );
}
